import { captureException as sentryCaptureException } from "@sentry/react"
import Dexie, { EntityTable, Table } from "dexie"

import { logger } from "@/utils/monitoring/logging"

import type { ClaimDetailBlob, ClaimDetailLocal } from "./tables/claimDetails/claimDetails"
import type { ClaimMarkerLocal } from "./tables/claimMarkers/claimMarkers"
import type { ConfigLocal } from "./tables/config/config"
import type { LogItem } from "./tables/logs/logs"
import type { OrderedStepLocal } from "./tables/orderedSteps/orderedSteps"
import type { PhotoBlob, PhotoSlim, PhotoWithBinaryData } from "./tables/photos/photos"
import type { SessionLocal } from "./tables/session/session"
import type { VideoItem } from "./tables/videos/videos"
import { logDexieError } from "./utils/logDexieError"

class OfflineDatabase extends Dexie {
  public logs!: Table<LogItem>
  public videos!: EntityTable<VideoItem, "dexieId">
  public orderedSteps!: EntityTable<OrderedStepLocal, "dexieId">
  public photos!: EntityTable<PhotoSlim, "dexieId">
  public photoBlobs!: EntityTable<PhotoBlob, "dexieId">
  public session!: EntityTable<SessionLocal, "dexieId">
  public config!: EntityTable<ConfigLocal, "dexieId">
  public claimMarkers!: EntityTable<ClaimMarkerLocal, "dexieId">
  public claimDetails!: EntityTable<ClaimDetailLocal, "dexieId">
  public claimDetailBlobs!: EntityTable<ClaimDetailBlob, "dexieId">

  constructor() {
    super("OfflineDB")

    this.version(9)
      .stores({ blobs: "&url" })
      .upgrade(async () => {
        await this.table("blobs").clear()
      })

    this.version(10).stores({ blobs: "&url", logs: "++, timestamp" })

    this.version(11).stores({
      blobs: "&url",
      logs: "++, timestamp",
      videos: "++dexieId,&videoId,sessionId,type,mimeType,arrayBuffer,uploaded,readyToUpload",
    })

    // v12: drops blobs, adds orderedSteps, photos, session, config + upload state on videos
    this.version(12)
      .stores({
        logs: "++, timestamp",
        videos: "++dexieId,&videoId,sessionId,type,uploaded,readyToUpload,uploadProgress,uploadFailed",
        orderedSteps: "++dexieId,&[name+sessionId],sessionId",
        photos: "++dexieId,&id,sessionId,deleted,uploadedAt,uploadProgress,uploadFailed",
        session: "++dexieId,&id",
        config: "++dexieId,&sessionId",
      })
      .upgrade(async (tx) => {
        // Set default upload state on existing videos (photos/orderedSteps/session/config are new — no migration needed)
        await tx
          .table("videos")
          .toCollection()
          .modify((video: VideoItem) => {
            if (video.uploadProgress === undefined) {
              video.uploadProgress = video.uploaded === 1 ? 101 : -1
            }
            if (video.uploadFailed === undefined) {
              video.uploadFailed = false
            }
          })
      })

    // v13: move photo binaries out of `photos` into a dedicated `photoBlobs` table (keyed by photo id).
    // Keeps `photos` metadata-only so list/count/pending queries no longer deserialize MBs of blobs.
    this.version(13)
      .stores({
        logs: "++, timestamp",
        videos: "++dexieId,&videoId,sessionId,type,uploaded,readyToUpload,uploadProgress,uploadFailed",
        orderedSteps: "++dexieId,&[name+sessionId],sessionId",
        photos: "++dexieId,&id,sessionId,deleted,uploadedAt,uploadProgress,uploadFailed",
        session: "++dexieId,&id",
        config: "++dexieId,&sessionId",
        photoBlobs: "++dexieId,&photoId,sessionId",
      })
      .upgrade(async (tx) => {
        const photosTable = tx.table<PhotoWithBinaryData, number>("photos")
        const blobsTable = tx.table<Omit<PhotoBlob, "dexieId">>("photoBlobs")
        const keys = await photosTable.toCollection().primaryKeys()

        // Process one photo at a time
        for (const key of keys) {
          const photo = await photosTable.get(key)
          if (!photo) continue

          const hasData = !!photo.data
          const hasThumbnailData = !!photo.thumbnailData

          if (hasData || hasThumbnailData) {
            await blobsTable.put({
              photoId: photo.id,
              sessionId: photo.sessionId,
              data: photo.data,
              mime: photo.mime,
              thumbnailData: photo.thumbnailData,
              thumbnailMime: photo.thumbnailMime,
            })
          }

          // clean up binary data from photos table
          delete photo.data
          delete photo.mime
          delete photo.thumbnailData
          delete photo.thumbnailMime

          // save metadata back to photos table (without binary data) so we don't lose any other fields
          const metaOnly = { ...photo, hasData, hasThumbnailData }
          await photosTable.put(metaOnly)
        }
      })

    // v14: new claim step local tables (markers + details)
    this.version(14).stores({
      logs: "++, timestamp",
      videos: "++dexieId,&videoId,sessionId,type,uploaded,readyToUpload,uploadProgress,uploadFailed",
      orderedSteps: "++dexieId,&[name+sessionId],sessionId",
      photos: "++dexieId,&id,sessionId,deleted,uploadedAt,uploadProgress,uploadFailed",
      session: "++dexieId,&id",
      config: "++dexieId,&sessionId",
      photoBlobs: "++dexieId,&photoId,sessionId",
      claimMarkers: "++dexieId,&id,sessionId,photoId,deleted",
      claimDetails: "++dexieId,&id,sessionId,markerId,deleted",
      claimDetailBlobs: "++dexieId,&detailId,sessionId",
    })

    // v15: videoId -> id
    this.version(15)
      .stores({
        videos: "++dexieId,&id,sessionId,type,uploaded,readyToUpload,uploadProgress,uploadFailed",
      })
      .upgrade(async (tx) => {
        await tx
          .table("videos")
          .toCollection()
          .modify((video: { id?: string; videoId?: string }) => {
            video.id ??= video.videoId
            delete video.videoId
          })
      })
  }

  public clearAll = async (): Promise<void> => {
    try {
      await Promise.all([
        this.photos.clear(),
        this.photoBlobs.clear(),
        this.orderedSteps.clear(),
        this.claimMarkers.clear(),
        this.claimDetails.clear(),
        this.claimDetailBlobs.clear(),
      ])
    } catch (error) {
      logDexieError(error)
    }
  }
}

export const dexie = new OfflineDatabase()

// https://stackoverflow.com/questions/56496296/how-do-i-fix-firestore-sdk-hitting-an-internal-error-was-encountered-in-the-ind/56496297#56496297
window.onerror = function (error) {
  if (
    typeof error === "string" &&
    error.indexOf("An internal error was encountered in the Indexed Database server") >= 0
  ) {
    logger.info("handled Indexed Database exception by reloading window.")
    sentryCaptureException(error)
    // Refresh the page to restore IndexedDb to a working state.
    window.location.reload()
  }
}
